import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  isEmailLinkCallback,
  getStoredEmailForSignIn,
  completeEmailLinkSignIn,
} from "@/lib/firebase";

// CRITICAL: We do NOT auto-consume the OOB code on mount. Gmail's URL scanner
// visits the link before the user clicks — auto-consuming would burn the
// one-time code. Instead we require an explicit user click.
export default function FinishSignIn() {
  const navigate = useNavigate();
  const { celebrateLogin } = useAuth();
  const [isLink, setIsLink] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setIsLink(isEmailLinkCallback());
    setEmail(getStoredEmailForSignIn());
  }, []);

  const confirm = async () => {
    if (!email) {
      toast.error("Please enter the email you signed up with");
      return;
    }
    setBusy(true);
    try {
      await completeEmailLinkSignIn(email.trim());
      toast.success("Signed in — welcome to NaturePulse");
      celebrateLogin();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.message || "Sign-in link invalid or expired");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
      <div className="max-w-md w-full">
        <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-3">
          NaturePulse · secure sign-in
        </div>
        <h1 className="font-serif-display text-5xl leading-[0.95] text-[color:var(--np-text)]">
          Confirm your email.
        </h1>
        <p className="mt-4 text-sm text-[color:var(--np-text-2)] leading-relaxed">
          For security, we don't automatically consume the link — some email
          providers (Gmail included) pre-scan URLs and would burn your one-time
          code. Tap the button below to complete sign-in.
        </p>

        {!isLink && (
          <div className="mt-6 border border-red-300 bg-red-50 text-red-800 p-4 text-sm">
            This URL is not a valid sign-in link. Please request a fresh link from the sign-in modal.
          </div>
        )}

        <div className="mt-6 space-y-3">
          <label className="block font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)]">
            Email address used
          </label>
          <input
            data-testid="finish-signin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@planet.earth"
            className="w-full border border-black/20 bg-transparent px-4 py-3 font-serif-display text-lg focus:outline-none focus:border-[color:var(--np-brand)]"
          />
          <button
            data-testid="finish-signin-confirm"
            onClick={confirm}
            disabled={busy || !isLink}
            className="w-full rounded-full bg-[color:var(--np-text)] text-[color:var(--np-bg)] px-6 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Confirming…" : "Confirm my email"}
          </button>
        </div>
      </div>
    </div>
  );
}
